import _ from "lodash"
export default class CesiumManagerWrapper {
    static maxAttempts = 4;
    static async initScene(elementId, {
        users,
        sceneType,
        hostInUse,
        windBearing,
        staticLabels,
        chartType,
        activityIcon,
        isTracksAlive,
        sceneId,
        contestIdInUse,
        externalTrackInUse
    }) {
        let attempts = 0;
        let needRun = true;
        do {
            const isRun = await CesiumManagerWrapper._attemptInitPlayer(elementId, {
                users,
                sceneType,
                hostInUse,
                windBearing,
                staticLabels,
                chartType,
                activityIcon,
                isTracksAlive,
                sceneId,
                contestIdInUse,
                externalTrackInUse
            });
            needRun = attempts < this.maxAttempts && isRun == false;

            if (needRun) {
                attempts++;
                await sleep(1000);
            }
        } while (needRun);

    }
    static async _attemptInitPlayer(elementId, {
        users,
        sceneType,
        hostInUse,
        windBearing,
        staticLabels,
        chartType,
        activityIcon,
        isTracksAlive,
        sceneId,
        contestIdInUse,
        externalTrackInUse
    }) {
        if (typeof CesiumManager !== 'undefined') {
            // Quick validation - defer heavy checks to CesiumManager
            if (!users || !Array.isArray(users) || users.length === 0) {
                console.error("CesiumManagerWrapper: Invalid or empty users data");
                return false;
            }

            // Enhanced validation to detect geometry data issues
            const validatedUsers = users.filter(user => {
                if (!user || !user.track) {
                    return false;
                }
                
                // Check basic structure
                if (!user.track.raw_points_data || !user.track.raw_points_data.points) {
                    return user.track.raw_points_data === null || user.track.raw_points_data === undefined;
                }
                
                // Check for null points_header which causes CesiumManager crashes
                if (user.track.raw_points_data.points_header === null) {
                    console.warn(`Filtering out user ${user.userId || 'unknown'} due to null points_header`);
                    return false;
                }
                
                // Check for geometry data consistency
                const points = user.track.raw_points_data.points;
                if (!Array.isArray(points) || points.length === 0) {
                    return false;
                }
                
                // Validate that all points have consistent attribute structure
                const firstPoint = points[0];
                if (!firstPoint || typeof firstPoint !== 'object') {
                    return false;
                }
                
                const expectedKeys = Object.keys(firstPoint);
                const hasConsistentAttributes = points.every(point => {
                    if (!point || typeof point !== 'object') return false;
                    const pointKeys = Object.keys(point);
                    return expectedKeys.length === pointKeys.length && 
                           expectedKeys.every(key => pointKeys.includes(key));
                });
                
                if (!hasConsistentAttributes) {
                    console.warn(`Filtering out user ${user.userId || 'unknown'} due to inconsistent geometry attributes`);
                    return false;
                }
                
                return true;
            });

            if (validatedUsers.length === 0) {
                console.error("No valid users after enhanced validation");
                return false;
            }
            
            if (validatedUsers.length < users.length) {
                const filteredCount = users.length - validatedUsers.length;
                console.warn(`Filtered out ${filteredCount} user(s) with invalid geometry data. Proceeding with ${validatedUsers.length} valid users.`);
            }

            try {
                await CesiumManager.initScene(elementId, {
                    users: validatedUsers,
                    sceneType: sceneType,
                    windBearing: windBearing,
                    isTracksAlive: isTracksAlive,
                    staticLabels: staticLabels,
                    pathsLength: window.data.path_length || 2000,
                    isFpvModeByDefault: window.data.start_in_fpv_mode === "true" ? true : null,
                    hostInUse: hostInUse,
                    endTrackOffset: window.data.end_track_offset || 10,
                    offlineSetAfterSeconds: window.data.offline_set_after || 30,
                    chartType: chartType || window.data.chart_type || null,
                    activityIcon: activityIcon || window.data.activity_icon || null,
                    showWindIndicator: window.data.show_wind_indicator || window.data.session.show_wind_indicator || null,
                    groundOffset: window.data.ground_offset || 40,
                    activityIcon: window.data.activity_icon || null,
                    externalTrackInUse: externalTrackInUse || false,
                    // contest: sceneId !== undefined ? await this.getSceneContest(sceneId) : await this.getTrackContest(_.get(users, '[0].track.id'), parseInt(_.get(users, '[0].userId')))
                    contest: contestIdInUse !== undefined ? await this.getContestData(contestIdInUse) : null
                });
            } catch (error) {
                console.warn("Error initializing STL Player with", validatedUsers.length, "users:", error.message);                
                
                throw error;
            }
            return true;
        }
        return false;
    }

    static async getContestData(contestId) {
        if (!contestId) {
            return null;
        }
        const ajaxData = {
            url: window.application.getAPIUrl() + "/v1/contest_task/" + contestId + "/cesium_contest",
            method: "GET",
            beforeSend: function (request) {
                request.setRequestHeader("X-STL-Token", cookies.get("STL-Token"));
            },
        };

        let data;
        try {
            data = (await $.ajax(ajaxData));
        } catch (e) {
            data = null;
        }
        return data;
    }

    static async getTrackContest(trackId, userId) {
        if (!trackId) {
            return null;
        }
        const ajaxData = {
            url: window.application.getAPIUrl() + "/v1/users/" + userId + "/track/" + trackId + "/contest",
            method: "GET",
            beforeSend: function (request) {
                request.setRequestHeader("X-STL-Token", cookies.get("STL-Token"));
            },
        };

        let data;
        try {
            data = (await $.ajax(ajaxData));
        } catch (e) {
            data = null;
        }
        return data;
    }

    static async getSceneContest(sceneId) {
        if (!sceneId) {
            return null;
        }
        const ajaxData = {
            url: window.application.getAPIUrl() + "/v1/scene/" + sceneId + "/contest",
            method: "GET",
            beforeSend: function (request) {
                request.setRequestHeader("X-STL-Token", cookies.get("STL-Token"));
            },
        };

        let data;
        try {
            data = (await $.ajax(ajaxData));
        } catch (e) {
            data = null;
        }
        return data;
    }
    static destroyWidget() {
        CesiumManager.destroy();
    }
}
const sleep = (timeInMS) => new Promise(resolve => setTimeout(resolve, timeInMS));